import { motion } from "framer-motion";
import { RevealLine, Rise, SectionLabel } from "./Motion";

const services = [
  {
    no: "S1",
    title: "Transmission Lines & Substations",
    scope: "Up to 400KV systems",
    desc: "EPC construction of extra-high-voltage transmission lines and substations, delivered end-to-end from design to commissioning.",
    img: "https://images.unsplash.com/photo-1566543042578-5db95b961375?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHx0cmFuc21pc3Npb24lMjB0b3dlcnMlMjBza3l8ZW58MHx8fHwxNzg0MzUxMTIxfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    no: "S2",
    title: "Renewable Energy Projects",
    scope: "Up to 100MW · Solar, Wind & Hybrid",
    desc: "Full-scope EPC for utility-scale solar, wind and hybrid generation — engineered for maximum yield and grid reliability.",
    img: "https://images.unsplash.com/photo-1594818379496-da1e345b0ded?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVscyUyMGFlcmlhbHxlbnwwfHx8fDE3ODQzNTExMjF8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    no: "S3",
    title: "Mission-Critical Data Centres",
    scope: "High-availability facilities",
    desc: "Construction of resilient, power-dense data centre infrastructure built for uptime, redundancy and demanding operational standards.",
    img: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBkYXRhJTIwY2VudGVyfGVufDB8fHx8MTc4NDM1MTEyMXww&ixlib=rb-4.1.0&q=85",
  },
  {
    no: "S4",
    title: "Civil & Infrastructure",
    scope: "Large-scale projects",
    desc: "Heavy civil and infrastructure execution — bridges, roads and enabling works — delivered with disciplined project management.",
    img: "https://images.unsplash.com/photo-1529926691761-20fb82067c71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBpbmZyYXN0cnVjdHVyZSUyMGJyaWRnZSUyMGNvbnN0cnVjdGlvbnxlbnwwfHx8fDE3ODQzNTExNTB8MA&ixlib=rb-4.1.0&q=85",
  },
];

function ServiceCard({ s, i }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-8%" }}
      transition={{ duration: 0.8, delay: (i % 2) * 0.12, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`service-card-${i}`}
      className="group relative h-[440px] overflow-hidden rounded-sm border border-vg-border bg-vg-bgalt lg:h-[500px]"
    >
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={s.img}
          alt={s.title}
          className="h-full w-full scale-105 object-cover opacity-90 transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-100"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-vg-primary via-vg-primary/50 to-transparent transition-opacity duration-500 group-hover:from-vg-primary" />
      </div>

      <div className="relative z-10 flex h-full flex-col justify-between p-8 lg:p-10">
        <div className="flex items-center justify-between">
          <span className="font-display text-sm font-medium tracking-[0.2em] text-vg-accent">
            {s.no}
          </span>
          <span className="rounded-full border border-white/25 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-white/80">
            EPC
          </span>
        </div>

        <div>
          <div className="mb-2 text-xs font-medium uppercase tracking-[0.2em] text-vg-accent">
            {s.scope}
          </div>
          <h3 className="font-display text-2xl font-semibold leading-tight text-white lg:text-3xl">
            {s.title}
          </h3>
          <p className="mt-3 max-h-0 overflow-hidden text-sm leading-relaxed text-white/80 opacity-0 transition-all duration-500 group-hover:max-h-40 group-hover:opacity-100">
            {s.desc}
          </p>
        </div>
      </div>
    </motion.article>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      className="relative bg-vg-bgalt py-24 lg:py-36"
      data-testid="services-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <SectionLabel>02 — Capabilities</SectionLabel>
            <h2 className="mt-8 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-vg-primary sm:text-5xl lg:text-6xl">
              <RevealLine>Four disciplines.</RevealLine>
              <RevealLine delay={0.08}>One turnkey partner.</RevealLine>
            </h2>
          </div>
          <Rise delay={0.15} className="max-w-md text-base leading-relaxed text-vg-muted">
            From high-voltage transmission to renewable generation and critical
            infrastructure — every project delivered under a single accountable
            EPC umbrella.
          </Rise>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8">
          {services.map((s, i) => (
            <ServiceCard key={s.title} s={s} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
